import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../features/auth/AuthContext'
import type { AppRole } from '../../features/auth/auth.types'
import { appConfig } from '../../config/app'
import { workspaceNavigation } from '../../config/navigation'
import ThemeCustomizer from './ThemeCustomizer'
import WorkspaceSidebar from './WorkspaceSidebar'
import WorkspaceTopbar from './WorkspaceTopbar'
import { usePublicSettings } from '../../features/settings/usePublicSettings'

const roleLabels: Record<AppRole, string> = {
  patient: 'Patient portal',
  doctor: 'Doctor workspace',
  admin: 'Administration',
}

export default function WorkspaceLayout({ role }: { role: AppRole }) {
  const { data: branding } = usePublicSettings()
  const [mini, setMiniState] = useState(
    () => localStorage.getItem(appConfig.storageKeys.sidebarMini) === 'true',
  )
  const [mobileOpen, setMobileOpen] = useState(false)
  const [customizerOpen, setCustomizerOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  useEffect(() => {
    setMobileOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  const setMini = (value: boolean) => {
    setMiniState(value)
    localStorage.setItem(appConfig.storageKeys.sidebarMini, String(value))
  }

  const toggleSidebar = () =>
    window.matchMedia('(max-width: 991.98px)').matches
      ? setMobileOpen((value) => !value)
      : setMini(!mini)

  const signOut = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const userName = user?.name ?? 'Signed-in user'
  const roleLabel = roleLabels[role]

  return (
    <div className={`admin-shell ${mini ? 'sidebar-mini' : ''}`}>
      <WorkspaceSidebar
        items={workspaceNavigation[role]}
        mini={mini}
        mobileOpen={mobileOpen}
        userName={userName}
        roleLabel={roleLabel}
        onClose={() => setMobileOpen(false)}
        onSignOut={signOut}
      />
      <div className="admin-main">
        <WorkspaceTopbar
          userName={userName}
          roleLabel={roleLabel}
          onToggleSidebar={toggleSidebar}
          onOpenCustomizer={() => setCustomizerOpen(true)}
        />
        <main className="dashboard-content">
          <Outlet />
        </main>
        <footer className="admin-footer">
          <span>
            © {new Date().getFullYear()}{' '}
            {branding?.site_name || appConfig.name}
          </span>
          <span>
            {branding?.footer_text || 'Private care, accessible design'}
          </span>
        </footer>
      </div>
      <ThemeCustomizer
        open={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        mini={mini}
        onMiniChange={setMini}
      />
    </div>
  )
}
